import * as React from 'react'
import { useEffect, useMemo } from 'react'

import { AppointmentSlot } from './AppointmentSlot'
import { useSchedulingWizard } from '../wizardContext'
import type { CommitHandler, GetResultsHandler } from './typings'
import { LoadingSpinner } from '@sales-center/components'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Alert from '@mui/material/Alert'
import CachedIcon from '@mui/icons-material/Cached'
import Button from '@mui/material/Button'
import { Theme } from '@mui/material/styles/createTheme'
import Stack from '@mui/material/Stack'

type ContentProps = {
  perPage?: number
  commitResult: CommitHandler
  getResults: GetResultsHandler
}

export function Content(props: ContentProps) {
  const { getResults, perPage = 3, commitResult } = props

  const [{ loading, response, page, mode, recurringServices }] =
    useSchedulingWizard()

  const items = useMemo(() => {
    const startIdx = page * perPage
    const endIdx = startIdx + perPage

    return response?.items.slice(startIdx, endIdx) ?? []
  }, [page, perPage, response?.items])

  useEffect(() => void getResults(), [getResults])

  const onRefresh = React.useCallback(() => {
    getResults()
  }, [getResults])

  const toCreate =
    mode === 'service'
      ? recurringServices
        ? 'a service setup with an initial service order'
        : 'a service order'
      : mode === 'free'
        ? 'an estimate order'
        : 'a service order'

  return (
    <Stack sx={{ px: 2 }}>
      <Alert severity="info">
        <Typography variant="body2">
          Selecting a time will create {toCreate} on the selected location
        </Typography>
      </Alert>
      <Box
        display="flex"
        alignItems={'center'}
        justifyContent="space-between"
        mt={1.2}
        pb={1.2}
        sx={{ borderBottom: '1px solid #E0E0E0' }}
      >
        <Typography variant="subtitle3">Results</Typography>
        <Button onClick={onRefresh} variant="outlined">
          <CachedIcon
            sx={{
              marginRight: (theme: Theme) => theme.spacing(0.5),
              fontSize: '20px',
            }}
          />
          Refresh results
        </Button>
      </Box>
      {loading ? (
        <Box
          sx={{
            flexGrow: 1,
            height: '30rem',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <LoadingSpinner />
        </Box>
      ) : (
        items.map((r) => (
          <AppointmentSlot
            key={r.resultId}
            result={r}
            commitResult={commitResult}
          />
        ))
      )}
    </Stack>
  )
}
